import { type DialogProps, Slide, type SxProps, type Theme } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef, type ReactElement, type Ref } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/** Paper of a sheet pinned to the bottom edge, for a `Dialog` or a bottom `Drawer`. */
export const bottomSheetPaperSx = {
  borderRadius: 2,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  borderTop: 1,
  borderColor: 'elevation.outlined',
  // Grab handle.
  '&::before': {
    content: '""',
    display: 'block',
    flexShrink: 0,
    width: 36,
    height: 4,
    borderRadius: 1,
    bgcolor: 'action.disabled',
    mx: 'auto',
    mt: 1,
  },
} as const;

export const bottomSheetSx: SxProps<Theme> = {
  '& .MuiDialog-container': { alignItems: 'flex-end' },
  '& .MuiDialog-paper': { m: 0, width: '100%', maxWidth: '100%', maxHeight: '92dvh', ...bottomSheetPaperSx },
};

/** Spread onto an MUI `Dialog` after its own `sx` and `slots`. */
export const bottomSheetDialogProps: Partial<DialogProps> = {
  slots: { transition: SlideUpTransition },
  sx: bottomSheetSx,
};

const NO_SHEET_PROPS: Partial<DialogProps> = {};

/** Below `md`, a dialog opens as a sheet sliding up from the bottom of the screen; `sheetProps` is empty on desktop. */
export function useBottomSheet(): { sheetProps: Partial<DialogProps> } {
  return { sheetProps: useIsMobile() ? bottomSheetDialogProps : NO_SHEET_PROPS };
}
